import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { isGlobalFeatureOn, getCachedGlobalFeatureFlagsForSync } from '../../shell/feature-flags/globalFeatureFlagsService';
import {
  validateCashMovementInput,
  type CashMovementCategory,
  type CashMovementDirection,
} from './cashOutflowCategories';
import { bumpCashSessionChangeSignal } from './cashSessionChangeSignal';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { isExpensesModuleEnabled } from '../../expenses/infra/expensesRepoRead';
import { cashCommandErrorFromRaw, mapCashCommandError } from './mapCashCommandError';
import { newClientUuid } from '../../shared/newClientUuid';

// Read-side queries viven en cashOutflowQueries.ts (leaf sin sync/expenses) para
// cortar el require cycle expensesRepoSettlement → cashOutflowRepo → expensesRepo.
export {
  fetchRemoteOutflowsForSession,
  listActiveOutflowsForSessionRemoteFirst,
  listOutflowsForSessionRemoteFirst,
} from './cashOutflowQueries';
export type { CashOutflowRow } from './cashOutflowQueries';

export async function isCashOutflowsFeatureEnabled(): Promise<boolean> {
  const flags = getCachedGlobalFeatureFlagsForSync();
  return isGlobalFeatureOn(flags, 'cash_outflows');
}

/**
 * Movimiento manual de efectivo del turno (egreso | ingreso).
 * El server es `pos.apply_cash_outflow_command` con payload.direction
 * (default 'out' para clientes viejos sin el campo).
 */
const createCashMovement = async (params: {
  branchId: string;
  cashSessionId: string;
  direction: CashMovementDirection;
  category: CashMovementCategory;
  amount: number;
  note?: string;
  createdBy?: string;
  organizationId?: string;
}): Promise<string> => {
  const isInflow = params.direction === 'in';
  if (!(await isCashOutflowsFeatureEnabled())) {
    throw new Error('Los movimientos de caja no están disponibles en este momento.');
  }

  const validation = validateCashMovementInput({
    direction: params.direction,
    category: params.category,
    amount: params.amount,
    note: params.note,
  });
  if (!validation.ok) {
    throw new Error(validation.message);
  }

  // Solo egresos pasan por el settlement de Gastos — un ingreso manual no es
  // un expense (no tiene factura ni proveedor).
  if (!isInflow && (await isExpensesModuleEnabled()) && params.organizationId) {
    // Lazy: expensesRepo -> expenseIntake -> expensesRepoSettlement -> cashOutflowRepo
    // es una dependencia mutua real; el import dinámico rompe el require cycle.
    const { createExpenseWithCashSettlement } = await import('../../expenses/infra/expensesRepo');
    const result = await createExpenseWithCashSettlement({
      organizationId: params.organizationId,
      branchId: params.branchId,
      cashSessionId: params.cashSessionId,
      category: params.category,
      amount: params.amount,
      note: params.note,
      createdBy: params.createdBy,
    });
    bumpCashSessionChangeSignal();
    return result.outflowId;
  }

  const { submitCommand } = await import('../../sync/core/submitCommand');
  const { getMergedActiveCashSession } = await import('./resolveCashSummary');
  const active = await getMergedActiveCashSession(params.branchId, { force: true });
  if (!active || active.id !== params.cashSessionId) {
    throw new Error(
      isInflow
        ? 'No hay una caja abierta para registrar el ingreso.'
        : 'No hay una caja abierta para registrar el egreso.'
    );
  }
  const outflowId = newClientUuid();
  const createdAt = new Date().toISOString();
  const note = (params.note ?? '').trim() || null;
  const result = await submitCommand({
    entity: 'cash_outflow',
    operation: 'insert',
    payload: {
      outflowId,
      branchId: params.branchId,
      cashSessionId: params.cashSessionId,
      direction: params.direction,
      category: params.category,
      amount: params.amount,
      note,
      createdAt,
      createdBy: params.createdBy ?? null,
    },
  });
  if (!result.applied) {
    const ingestDetail = result.ingestError?.trim() || 'cash_command_unknown';
    const mapped = mapCashCommandError(ingestDetail);
    const error = cashCommandErrorFromRaw(ingestDetail);
    captureOpsFailure({
      module: 'cash',
      op: isInflow ? 'cash.inflow' : 'cash.outflow',
      error,
      expected: mapped.expected === true,
      ingestError: result.ingestError,
      applied: false,
      path: 'web-ram',
      itemId: outflowId,
      extra: {
        outflowId,
        direction: params.direction,
        branchId: params.branchId,
        sessionId: params.cashSessionId,
        code: mapped.code,
      },
    });
    throw error;
  }
  void logBusinessAuditEvent(isInflow ? 'reports.cash.inflow.create' : 'reports.cash.outflow.create', {
    branchId: params.branchId,
    sessionId: params.cashSessionId,
    outflowId,
    direction: params.direction,
    category: params.category,
    amount: params.amount,
    note,
    createdBy: params.createdBy ?? null,
  });
  bumpCashSessionChangeSignal();
  return outflowId;
};

export const createCashOutflow = async (params: {
  branchId: string;
  cashSessionId: string;
  category: CashMovementCategory;
  amount: number;
  note?: string;
  createdBy?: string;
  organizationId?: string;
}): Promise<string> => createCashMovement({ ...params, direction: 'out' });

export const createCashInflow = async (params: {
  branchId: string;
  cashSessionId: string;
  category: CashMovementCategory;
  amount: number;
  note?: string;
  createdBy?: string;
  organizationId?: string;
}): Promise<string> => createCashMovement({ ...params, direction: 'in' });

export const voidCashOutflow = async (params: {
  outflowId: string;
  voidedBy?: string;
  voidReason?: string;
  supervisorOverrideId?: string;
}): Promise<void> => {
  if (!(await isCashOutflowsFeatureEnabled())) {
    throw new Error('Los egresos de caja no están disponibles en este momento.');
  }

  const voidedAt = new Date().toISOString();
  const voidReason = (params.voidReason ?? '').trim() || null;
  const { submitCommand } = await import('../../sync/core/submitCommand');
  const result = await submitCommand({
    entity: 'cash_outflow_void',
    operation: 'insert',
    payload: {
      outflowId: params.outflowId,
      voidedAt,
      voidedBy: params.voidedBy ?? null,
      voidReason,
      ...(params.supervisorOverrideId
        ? { supervisorOverrideId: params.supervisorOverrideId }
        : {}),
    },
  });
  if (!result.applied) {
    const ingestDetail = result.ingestError?.trim() || 'cash_command_unknown';
    const mapped = mapCashCommandError(ingestDetail);
    const error = cashCommandErrorFromRaw(ingestDetail);
    captureOpsFailure({
      module: 'cash',
      op: 'cash.outflow_void',
      error,
      expected: mapped.expected === true,
      ingestError: result.ingestError,
      applied: false,
      path: 'web-ram',
      itemId: params.outflowId,
      extra: { outflowId: params.outflowId, code: mapped.code },
    });
    throw error;
  }
  void logBusinessAuditEvent('reports.cash.outflow.void', {
    outflowId: params.outflowId,
    voidReason,
    voidedBy: params.voidedBy ?? null,
    before: { status: 'active' },
    after: { status: 'voided' },
  });
  bumpCashSessionChangeSignal();
};
